import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IBaseSearchRequest, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { IBasketItem, IBasketItemSearchRequest, IOfflineBasketItem } from '../models/basket';
import { BasketService } from '../services/basket.service';
import { CurrentBasketService } from '../services/current-basket.service';
import { OfflineBasketStorageService } from '../storage/offline-basket-store.service';
import { BehaviorSubject, Observable, Subject, combineLatest, map, of, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';

@UntilDestroy()
@Component({
    selector: 'app-basket-admin',
    templateUrl: './basket-admin.component.html',
    styleUrls: ['./basket-admin.component.scss'],
    standalone: false
})
export class BasketAdminComponent implements OnInit {

  vm$: Observable<any>;
  loading: boolean = false;


  constructor(
    private basketService: BasketService,
    private currentBasketService : CurrentBasketService,
    private offlineBasketStore : OfflineBasketStorageService
  ) { 
    this.vm$ = combineLatest([
      this.gridState$.pipe(),
      this.searchRequest$.pipe(
        switchMap((request) => {
          this.loading = true;

          // Falls companyId null ist, leere Daten zurückgeben
          if (!request.companyId) {
            return of({ data: [], totalCount: 0 });
          }
    
          // Andernfalls API-Aufruf mit der aktualisierten Anfrage
          return this.basketService.listBasketItems(request);
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(res => {
          var gridData : GridDataResult = {
            data: res.data,
            total: res.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      )
    ]).pipe(
      map(([
        gridState,
        basketItems,
        searchRequest,
        pageChangeSub
      ]) => ({
        gridState,
        basketItems,
        searchRequest,
        pageChangeSub
      }))
    )
  }

    /////////////
  // getter //
  ///////////

  get pageSizes() {
    return PageSize;
  }


  /////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBasketItemSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    companyId: null
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  public basketItems: IGridResult<IBasketItem[]> = {
    data: [],
    total: 0
  };

  public currentCompany$ = this.currentBasketService.currentCompany$;

  offlineBasketItems: IOfflineBasketItem[];

  ngOnInit(): void {
    this.initBasketItems();
    this.loadLocalBasket();
  }

  loadLocalBasket(){
    this.offlineBasketItems = this.offlineBasketStore.getProductsAsArray();
  }

  reloadBasket() {
    this.loadLocalBasket();
    
    // Trigger basket reload by updating searchRequest$
    this.searchRequest$.next({
      ...this.searchRequest$.value, // Keep existing values
      pageNumber: 0 // Optionally reset to first page
    });
  }

  initBasketItems(){
    this.currentCompany$
    .pipe(untilDestroyed(this))
    .subscribe(company => {
      if(company && company.tenantId !== null){
        this.loadLocalBasket();       

        // Trigger basket reload by updating searchRequest$
        this.searchRequest$.next({
          ...this.searchRequest$.value,
          companyId: company.id
        });
      }
    })
  }
}

